import { useState } from "react";
import { getErrorMessage } from "@/lib/errors";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare, Bug, Lightbulb, CheckCircle2, Loader2, HelpCircle, Wand2 } from "lucide-react";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/ui/page-header";

type FeedbackType = "bug" | "feature" | "general";

const typeOptions: { value: FeedbackType; label: string; icon: React.ElementType; description: string }[] = [
  { value: "bug", label: "Bug Report", icon: Bug, description: "Something isn't working" },
  { value: "feature", label: "Feature Request", icon: Lightbulb, description: "Suggest an improvement" },
  { value: "general", label: "General Feedback", icon: MessageSquare, description: "Share your thoughts" },
];

const promptCues = [
  {
    id: "confusing",
    icon: HelpCircle,
    label: "What confused you?",
    hint: "Was there a moment where you weren't sure what to do next, or what a number meant? Tell us where you were and what you expected.",
    type: "general" as FeedbackType,
  },
  {
    id: "improvements",
    icon: Wand2,
    label: "What could we improve?",
    hint: "Describe a feature or change that would make BudgeAI work better for you. The more detail, the better!",
    type: "feature" as FeedbackType,
  },
];

const Feedback = () => {
  const { toast } = useToast();
  const [type, setType] = useState<FeedbackType>("general");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activePrompt, setActivePrompt] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const meta = {
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      };

      const { error } = await supabase.from("feedback").insert({
        user_id: user?.id ?? null,
        type,
        message: message.trim(),
        page: window.location.pathname,
        meta,
      });

      if (error) throw error;

      setSubmitted(true);
      setMessage("");
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error) || "Failed to submit feedback",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center justify-center py-20 text-center gap-4"
        >
          <div className="p-4 bg-income/10 rounded-full">
            <CheckCircle2 className="h-10 w-10 text-income-text" />
          </div>
          <h2 className="text-2xl font-bold">Thanks for the feedback!</h2>
          <p className="text-muted-foreground max-w-sm">
            Your input helps make BudgeAI better. We read every submission.
          </p>
          <Button variant="outline" onClick={() => setSubmitted(false)} className="mt-2">
            Submit another
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-2xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <PageHeader
          title="Feedback"
          description="Found a bug or have an idea? Let us know."
        />

        {/* Welcome note */}
        <div className="rounded-xl border border-primary/20 bg-primary/5 px-5 py-4 space-y-1">
          <p className="text-sm font-medium text-foreground">
            Hi there — thanks for checking out our app! 👋
          </p>
          <p className="text-sm text-muted-foreground">
            BudgeAI is still growing, and your feedback directly shapes what we build next.
            Whether you spotted a bug, have a feature idea, or just want to share a thought — we genuinely read every message and appreciate you taking the time.
          </p>
          <p className="text-sm text-muted-foreground">
            If you'd prefer a direct conversation, you can also reach me at{" "}
            <a href="mailto:osobaopeyemi@gmail.com" className="text-primary-text hover:underline underline-offset-4">
              osobaopeyemi@gmail.com
            </a>.
          </p>
          <p className="text-sm text-muted-foreground">
            — Opeyemi
          </p>
        </div>

        {/* Prompt cues */}
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Not sure what to say? Try one of these</p>
          <div className="flex gap-2 flex-wrap">
            {promptCues.map(({ id, icon: Icon, label, hint, type: promptType }) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setActivePrompt(id);
                  setType(promptType);
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                  activePrompt === id
                    ? "border-primary bg-primary/10 text-primary-text"
                    : "border-border/40 bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </button>
            ))}
          </div>
          {activePrompt && (
            <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">
              💡 {promptCues.find(p => p.id === activePrompt)?.hint}
            </p>
          )}
        </div>

        {/* Type selector cards */}
        <div className="grid grid-cols-3 gap-3">
          {typeOptions.map(({ value, label, icon: Icon, description }) => (
            <button
              key={value}
              type="button"
              onClick={() => { setType(value); setActivePrompt(null); }}
              className={`p-4 rounded-xl border text-left transition-all duration-200 ${
                type === value
                  ? "border-primary bg-primary/10"
                  : "border-border/40 bg-card hover:border-primary/40"
              }`}
            >
              <Icon className={`h-5 w-5 mb-2 ${type === value ? "text-primary-text" : "text-muted-foreground"}`} />
              <p className={`text-sm font-medium ${type === value ? "text-primary-text" : ""}`}>{label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
            </button>
          ))}
        </div>

        <Card className="border-border/40">
          <CardHeader className="pb-3 pt-5">
            <CardTitle className="text-lg font-bold">
              {typeOptions.find(t => t.value === type)?.label}
            </CardTitle>
            <CardDescription>
              {type === "bug" && "Describe what happened and how to reproduce it."}
              {type === "feature" && "Describe the feature and why it would be useful."}
              {type === "general" && "Share anything on your mind about the app."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  placeholder={
                    type === "bug"
                      ? "Steps to reproduce: 1. Go to... 2. Click... 3. See error..."
                      : type === "feature"
                      ? "I'd love to see..."
                      : "Share your thoughts..."
                  }
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  className="resize-none bg-muted/30 border-border/50 focus:border-primary/50"
                  required
                />
              </div>

              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-muted-foreground">
                  Feedback is anonymous — your user ID (not your name) and current page are attached automatically.
                </p>
                <Button
                  type="submit"
                  disabled={submitting || !message.trim()}
                  className="gap-2"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MessageSquare className="h-4 w-4" />
                  )}
                  {submitting ? "Sending..." : "Send Feedback"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Feedback;
